import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  PlayCircle,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { BADGE_TONES } from "@/lib/badge-tones";

const DAY_OPTIONS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

type DayOfWeek = (typeof DAY_OPTIONS)[number];
type ScheduleType = "daily" | "weekly" | "interval";

interface CronLog {
  _id: string;
  jobName: string;
  startedAt: number;
  completedAt?: number;
  status: string;
  recordsProcessed?: number;
  errorMessage?: string;
  executionTimeMs?: number;
  createdAt: number;
}

interface CronConfigChangeLog {
  _id: string;
  jobName: string;
  changeType: "schedule_update" | "active_toggle" | "manual_trigger" | string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  reason?: string;
  changedByName?: string;
  changedByEmail?: string;
  changedByAuthUserId?: string;
  createdAt: number;
}

type ImpactLevel = "high" | "medium" | "low";

interface CronConfigRow {
  _id: string;
  jobName: string;
  displayName: string;
  description?: string;
  category?: string;
  impactLevel?: ImpactLevel;
  disableImpact?: string | null;
  schedule: string;
  scheduleType?: ScheduleType;
  hourUTC?: number;
  minuteUTC?: number;
  dayOfWeek?: DayOfWeek;
  intervalMinutes?: number;
  timezone?: string;
  isActive: boolean;
  effectiveIsActive: boolean;
  effectiveReason?: string | null;
  globalEnabled: boolean;
  globalReason?: string | null;
  registered: boolean;
  configSource: "persisted" | "default";
  lastRunAt?: number;
  lastRunStatus?: string;
  lastUpdatedByName?: string;
  lastUpdatedByEmail?: string;
  lastUpdatedByAuthUserId?: string;
  createdAt: number;
  updatedAt: number;
  recentLogs: CronLog[];
  recentConfigChanges: CronConfigChangeLog[];
}

type ExecutionStateKind =
  | "scheduled"
  | "manualOnly"
  | "adminDisabled"
  | "globalDisabled";

const CATEGORY_ORDER = [
  "Finance & Accounts Receivable",
  "Contracts & Billing",
  "Checkout & Inventory",
  "Operations & Fulfillment",
  "Market Data & Reporting",
  "Customer Lifecycle & Support",
  "Maintenance",
] as const;

const CATEGORY_DETAILS: Record<
  string,
  {
    description: string;
    badgeClass: string;
  }
> = {
  "Finance & Accounts Receivable": {
    description:
      "Collections, refunds, payment reminders, payout updates, and overdue account automation.",
    badgeClass: BADGE_TONES.emerald,
  },
  "Contracts & Billing": {
    description:
      "Carry-fee and contract lifecycle automation that keeps contract billing on schedule.",
    badgeClass: BADGE_TONES.violet,
  },
  "Checkout & Inventory": {
    description:
      "Checkout cleanup, reservation release, and stock-monitoring jobs that protect sellable inventory.",
    badgeClass: BADGE_TONES.sky,
  },
  "Operations & Fulfillment": {
    description:
      "Order-ops and logistics automation that keeps fulfillment workflows tidy.",
    badgeClass: BADGE_TONES.indigo,
  },
  "Market Data & Reporting": {
    description:
      "External pricing syncs and reporting jobs that keep internal visibility current.",
    badgeClass: BADGE_TONES.cyan,
  },
  "Customer Lifecycle & Support": {
    description:
      "Customer engagement and support hygiene workflows that improve follow-up without manual effort.",
    badgeClass: BADGE_TONES.orange,
  },
  Maintenance: {
    description:
      "Background cleanup and operational housekeeping tasks for long-term system health.",
    badgeClass: BADGE_TONES.slate,
  },
};

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function getCategoryBadgeClass(category?: string): string {
  return CATEGORY_DETAILS[category ?? ""]?.badgeClass ?? BADGE_TONES.slate;
}

function getImpactBadgeClass(impactLevel?: ImpactLevel): string {
  if (impactLevel === "high") return BADGE_TONES.red;
  if (impactLevel === "low") return BADGE_TONES.blue;
  return BADGE_TONES.amber;
}

function getImpactLabel(impactLevel?: ImpactLevel): string {
  if (impactLevel === "high") return "High";
  if (impactLevel === "low") return "Low";
  return "Medium";
}

function getExecutionState(
  config: Pick<
    CronConfigRow,
    "globalEnabled" | "globalReason" | "isActive" | "registered"
  >,
): {
  kind: ExecutionStateKind;
  label: string;
  badgeClass: string;
  description: string;
} {
  if (!config.globalEnabled) {
    return {
      kind: "globalDisabled",
      label: "Won't run",
      badgeClass: BADGE_TONES.red,
      description:
        config.globalReason ??
        "The global automation engine is off, so scheduled and manual runs are blocked.",
    };
  }

  if (!config.isActive) {
    return {
      kind: "adminDisabled",
      label: "Won't run",
      badgeClass: BADGE_TONES.zinc,
      description:
        "This job is turned off in admin controls, so scheduled and manual runs are blocked.",
    };
  }

  if (!config.registered) {
    return {
      kind: "manualOnly",
      label: "Manual only",
      badgeClass: BADGE_TONES.blue,
      description:
        "This workflow is not registered on a schedule. It can still be run manually while the global engine and admin toggle stay on.",
    };
  }

  return {
    kind: "scheduled",
    label: "Will run",
    badgeClass: BADGE_TONES.emerald,
    description:
      "Scheduled triggers and manual runs are both allowed for this deployment.",
  };
}

function estimateNextRun(config: CronConfigRow): string {
  const now = Date.now();

  if (!config.registered) {
    return "Manual only";
  }
  if (!config.globalEnabled) {
    return "Global kill switch off";
  }
  if (!config.isActive) {
    return "Disabled in admin";
  }

  if (config.scheduleType === "daily" && config.hourUTC !== undefined && config.minuteUTC !== undefined) {
    const next = new Date(now);
    next.setUTCHours(config.hourUTC, config.minuteUTC, 0, 0);
    if (next.getTime() <= now) {
      next.setUTCDate(next.getUTCDate() + 1);
    }
    return formatTimestamp(next.getTime());
  }

  if (
    config.scheduleType === "weekly" &&
    config.hourUTC !== undefined &&
    config.minuteUTC !== undefined &&
    config.dayOfWeek
  ) {
    const targetDayIndex = DAY_OPTIONS.indexOf(config.dayOfWeek);
    const next = new Date(now);
    const nowDay = next.getUTCDay();
    let daysToAdd = (targetDayIndex - nowDay + 7) % 7;

    const sameDayTarget = new Date(now);
    sameDayTarget.setUTCHours(config.hourUTC, config.minuteUTC, 0, 0);
    if (daysToAdd === 0 && sameDayTarget.getTime() <= now) {
      daysToAdd = 7;
    }

    next.setUTCDate(next.getUTCDate() + daysToAdd);
    next.setUTCHours(config.hourUTC, config.minuteUTC, 0, 0);
    return formatTimestamp(next.getTime());
  }

  if (config.scheduleType === "interval" && config.intervalMinutes !== undefined) {
    const base = config.lastRunAt ?? now;
    return formatTimestamp(base + config.intervalMinutes * 60 * 1000);
  }

  return "Best effort unavailable";
}

function statusBadge(status: string | undefined): React.ReactNode {
  if (!status || status === "never") {
    return <Badge className={BADGE_TONES.zinc}>Pending first run</Badge>;
  }
  if (status === "success") {
    return (
      <Badge className={BADGE_TONES.emerald}>
        <CheckCircle className="mr-1 h-3 w-3" />
        Success
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge className={BADGE_TONES.red}>
        <XCircle className="mr-1 h-3 w-3" />
        Failed
      </Badge>
    );
  }
  if (status === "skipped") {
    return <Badge className={BADGE_TONES.amber}>Skipped</Badge>;
  }
  if (status === "started") {
    return (
      <Badge className={BADGE_TONES.blue}>
        <Activity className="mr-1 h-3 w-3 animate-pulse" />
        Running
      </Badge>
    );
  }
  return <Badge className={BADGE_TONES.slate}>{status}</Badge>;
}

function changeTypeLabel(changeType: string): string {
  if (changeType === "schedule_update") return "Schedule updated";
  if (changeType === "active_toggle") return "Active state changed";
  if (changeType === "manual_trigger") return "Manual run triggered";
  return changeType;
}

function CronTableSkeleton() {
  return (
    <div>
      <div className="flex items-center gap-4 border-b px-4 py-2 text-xs font-medium text-muted-foreground">
        <div className="w-4" />
        <div className="flex-1">Name</div>
        <div className="hidden min-w-[180px] sm:block">Schedule</div>
        <div className="hidden min-w-[90px] md:block">Last run</div>
        <div className="hidden min-w-[170px] lg:block">Next run</div>
        <div className="hidden min-w-[90px] sm:block">Status</div>
        <div className="w-12 text-center">Active</div>
        <div className="w-48" />
      </div>
      {Array.from({ length: 6 }).map((_, idx) => (
        <div key={idx} className="flex items-center gap-4 border-b px-4 py-4 last:border-b-0">
          <Skeleton className="h-4 w-4" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-72" />
          </div>
          <Skeleton className="hidden h-4 min-w-[160px] sm:block" />
          <Skeleton className="hidden h-4 min-w-[80px] md:block" />
          <Skeleton className="hidden h-4 min-w-[150px] lg:block" />
          <Skeleton className="hidden h-5 min-w-[80px] rounded-full sm:block" />
          <Skeleton className="h-6 w-10 rounded-full" />
          <div className="flex w-48 gap-2">
            <Skeleton className="h-8 flex-1" />
            <Skeleton className="h-8 flex-1" />
          </div>
        </div>
      ))}
    </div>
  );
}

function LogsPanel({
  config,
  logs,
  configChanges,
}: {
  config: CronConfigRow;
  logs: CronLog[];
  configChanges: CronConfigChangeLog[];
}) {
  const executionState = getExecutionState(config);

  if (logs.length === 0 && configChanges.length === 0) {
    return (
      <div className="border-t">
        <div className="grid gap-3 border-b bg-muted/20 px-4 py-3 md:grid-cols-3">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              What it does
            </p>
            <p className="text-sm text-foreground">
              {config.description ?? "No description available yet."}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Will this run?
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={executionState.badgeClass}>{executionState.label}</Badge>
              <Badge className={getCategoryBadgeClass(config.category)}>
                {config.category ?? "Uncategorized"}
              </Badge>
              <Badge className={getImpactBadgeClass(config.impactLevel)}>
                {getImpactLabel(config.impactLevel)} impact
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{executionState.description}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              If turned off
            </p>
            <p className="text-sm text-muted-foreground">
              {config.disableImpact ?? "No operator guidance recorded for this automation yet."}
            </p>
          </div>
        </div>
        <div className="px-4 pb-4 pt-2 text-sm text-muted-foreground">
          No run or configuration history yet.
        </div>
      </div>
    );
  }

  return (
    <div className="border-t">
      <div className="grid gap-3 border-b bg-muted/20 px-4 py-3 md:grid-cols-3">
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            What it does
          </p>
          <p className="text-sm text-foreground">
            {config.description ?? "No description available yet."}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Will this run?
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={executionState.badgeClass}>{executionState.label}</Badge>
            <Badge className={getCategoryBadgeClass(config.category)}>
              {config.category ?? "Uncategorized"}
            </Badge>
            <Badge className={getImpactBadgeClass(config.impactLevel)}>
              {getImpactLabel(config.impactLevel)} impact
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{executionState.description}</p>
        </div>
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            If turned off
          </p>
          <p className="text-sm text-muted-foreground">
            {config.disableImpact ?? "No operator guidance recorded for this automation yet."}
          </p>
        </div>
      </div>

      <div className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Run history
      </div>
      {logs.length === 0 ? (
        <div className="px-4 py-2 text-xs text-muted-foreground">No run history yet.</div>
      ) : (
        <div className="divide-y">
          {logs.map((log) => (
            <div key={log._id} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-4 py-2 text-xs">
              <span className="min-w-[200px] text-muted-foreground">
                {formatTimestamp(log.startedAt)}
              </span>
              <span className="min-w-[90px]">{statusBadge(log.status)}</span>
              {log.executionTimeMs !== undefined && (
                <span className="text-muted-foreground">{formatDuration(log.executionTimeMs)}</span>
              )}
              {log.recordsProcessed !== undefined && (
                <span className="text-muted-foreground">{log.recordsProcessed} records</span>
              )}
              {log.errorMessage && (
                <span className="w-full text-red-600 dark:text-red-400">{log.errorMessage}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="border-b border-t px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Configuration changes
      </div>
      {configChanges.length === 0 ? (
        <div className="px-4 py-2 text-xs text-muted-foreground">No schedule or active-state changes recorded.</div>
      ) : (
        <div className="divide-y">
          {configChanges.map((change) => (
            <div key={change._id} className="flex flex-wrap items-start gap-x-3 gap-y-1 px-4 py-2 text-xs">
              <span className="min-w-[200px] text-muted-foreground">
                {formatTimestamp(change.createdAt)}
              </span>
              <span className="font-medium">{changeTypeLabel(change.changeType)}</span>
              <span className="text-muted-foreground">
                by {change.changedByName || change.changedByEmail || change.changedByAuthUserId || "admin"}
              </span>
              {change.reason && <span className="text-muted-foreground">Reason: {change.reason}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CronRow({ config }: { config: CronConfigRow }) {
  const [expanded, setExpanded] = React.useState(false);
  const [toggling, setToggling] = React.useState(false);
  const [triggering, setTriggering] = React.useState(false);
  const nextRun = estimateNextRun(config);
  const executionState = getExecutionState(config);

  const setCronActive = useMutation(api.automationCrons.setCronActive);
  const triggerCronNow = useMutation(api.automationCrons.triggerCronNow);

  async function handleToggle(newValue: boolean) {
    setToggling(true);
    try {
      await setCronActive({ jobName: config.jobName, isActive: newValue });
      toast.success(`${config.displayName} config ${newValue ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(`Failed to update: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setToggling(false);
    }
  }

  async function handleRunNow() {
    setTriggering(true);
    try {
      await triggerCronNow({ jobName: config.jobName });
      toast.success(`${config.displayName} triggered — check logs in a moment`);
    } catch (err) {
      toast.error(`Failed to trigger: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setTriggering(false);
    }
  }

  const actorText =
    config.lastUpdatedByName || config.lastUpdatedByEmail || config.lastUpdatedByAuthUserId;
  const runNowDisabled = triggering || !config.globalEnabled || !config.isActive;

  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-center gap-4 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="text-muted-foreground hover:text-foreground"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium leading-tight">{config.displayName}</span>
            <Badge className={executionState.badgeClass}>{executionState.label}</Badge>
            <Badge className={getCategoryBadgeClass(config.category)}>
              {config.category ?? "Uncategorized"}
            </Badge>
            <Badge className={getImpactBadgeClass(config.impactLevel)}>
              {getImpactLabel(config.impactLevel)} impact
            </Badge>
            {!config.registered && <Badge className={BADGE_TONES.blue}>No schedule registration</Badge>}
            {config.configSource === "default" && <Badge className={BADGE_TONES.zinc}>Default config</Badge>}
          </div>
          {config.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{config.description}</p>
          )}
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            <span className="font-medium text-foreground">Run behavior:</span> {executionState.description}
          </p>
          {actorText && (
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Last changed by {actorText} {config.updatedAt ? `• ${timeAgo(config.updatedAt)}` : ""}
            </p>
          )}
        </div>

        <div className="hidden min-w-[180px] items-center gap-1 text-xs text-muted-foreground sm:flex">
          <Clock className="h-3 w-3" />
          {config.schedule}
        </div>

        <div className="hidden min-w-[90px] text-xs text-muted-foreground md:block">
          {config.lastRunAt ? timeAgo(config.lastRunAt) : "Not run yet"}
        </div>

        <div className="hidden min-w-[170px] text-xs text-muted-foreground lg:block">
          {nextRun}
        </div>

        <div className="hidden min-w-[110px] sm:block">
          {statusBadge(config.lastRunStatus)}
        </div>

        <Switch
          checked={config.isActive}
          onCheckedChange={handleToggle}
          disabled={toggling}
          aria-label={`Toggle ${config.displayName} config`}
        />

        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleRunNow}
            disabled={runNowDisabled}
            className="shrink-0"
          >
            {triggering ? (
              <Activity className="mr-1 h-3 w-3 animate-pulse" />
            ) : (
              <PlayCircle className="mr-1 h-3 w-3" />
            )}
            Run now
          </Button>
        </div>
      </div>

      {expanded && (
        <LogsPanel
          config={config}
          logs={config.recentLogs}
          configChanges={config.recentConfigChanges ?? []}
        />
      )}

    </div>
  );
}

export default function AdminCronsPage() {
  const configs = useQuery(api.automationCrons.getAllCronConfigs);
  const seedCronConfigs = useMutation(api.automationCrons.seedCronConfigs);
  const cronConfigs = (configs ?? []) as CronConfigRow[];
  const globalCronEnabled = cronConfigs[0]?.globalEnabled ?? true;
  const globalCronReason = cronConfigs[0]?.globalReason ?? null;

  React.useEffect(() => {
    if (configs !== undefined && configs.length === 0) {
      void seedCronConfigs({});
    }
  }, [configs, seedCronConfigs]);

  const groupedCronConfigs = React.useMemo(() => {
    const groups = new Map<string, CronConfigRow[]>();
    const sortedConfigs = [...cronConfigs].sort((left, right) =>
      left.displayName.localeCompare(right.displayName),
    );

    for (const config of sortedConfigs) {
      const category = config.category ?? "Uncategorized";
      const existing = groups.get(category) ?? [];
      existing.push(config);
      groups.set(category, existing);
    }

    return [...groups.entries()].sort(([leftCategory], [rightCategory]) => {
      const leftIndex = CATEGORY_ORDER.indexOf(leftCategory as (typeof CATEGORY_ORDER)[number]);
      const rightIndex = CATEGORY_ORDER.indexOf(rightCategory as (typeof CATEGORY_ORDER)[number]);
      const normalizedLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
      const normalizedRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
      if (normalizedLeftIndex !== normalizedRightIndex) {
        return normalizedLeftIndex - normalizedRightIndex;
      }
      return leftCategory.localeCompare(rightCategory);
    });
  }, [cronConfigs]);

  const summary = React.useMemo(
    () =>
      cronConfigs.reduce(
        (acc, config) => {
          const executionState = getExecutionState(config);
          if (executionState.kind === "scheduled") {
            acc.scheduled += 1;
          } else if (executionState.kind === "manualOnly") {
            acc.manualOnly += 1;
          } else {
            acc.blocked += 1;
          }

          if ((config.impactLevel ?? "medium") === "high" && executionState.kind !== "scheduled") {
            acc.highImpactAttention += 1;
          }
          return acc;
        },
        {
          scheduled: 0,
          manualOnly: 0,
          blocked: 0,
          highImpactAttention: 0,
        },
      ),
    [cronConfigs],
  );

  const isLoading = configs === undefined;

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Admin automations
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">Automations</h1>
        <p className="max-w-3xl text-xs text-muted-foreground">
          Manage scheduled background jobs, see whether each workflow will actually run in this deployment, and review why a job is grouped under payments, inventory, fulfillment, reporting, support, or maintenance.
        </p>
      </div>

      {!isLoading && !globalCronEnabled && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="flex items-start gap-3 p-4 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
            <div className="space-y-1">
              <p className="font-medium text-amber-900 dark:text-amber-200">
                Automation engine is OFF for this deployment
              </p>
              <p className="text-amber-800 dark:text-amber-300">
                {globalCronReason ?? "All cron jobs are globally disabled."}
              </p>
              <p className="text-xs text-amber-700/90 dark:text-amber-400/90">
                Jobs below may look enabled in admin, but scheduled runs will still be skipped until the Convex env enables the global cron engine.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && cronConfigs.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Will run on schedule</CardDescription>
              <CardTitle className="text-2xl">{summary.scheduled}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">
              Global engine on, admin toggle on, and schedule registration present.
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Blocked right now</CardDescription>
              <CardTitle className="text-2xl">{summary.blocked}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">
              Disabled either by the global env gate or the per-job admin toggle.
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Manual only</CardDescription>
              <CardTitle className="text-2xl">{summary.manualOnly}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">
              Available to run manually, but not registered for automatic scheduling.
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>High-impact attention</CardDescription>
              <CardTitle className="text-2xl">{summary.highImpactAttention}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">
              High-impact automations that are not currently scheduled to execute.
            </CardContent>
          </Card>
        </div>
      )}

      {isLoading ? (
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Automation library</CardTitle>
            <CardDescription>Loading…</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <CronTableSkeleton />
          </CardContent>
        </Card>
      ) : cronConfigs.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            No cron jobs found. Seeding defaults…
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupedCronConfigs.map(([category, categoryConfigs]) => {
            const categoryMeta = CATEGORY_DETAILS[category] ?? {
              description: "Automation jobs grouped together for operator review.",
              badgeClass: BADGE_TONES.slate,
            };

            return (
              <Card key={category} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle className="text-base">{category}</CardTitle>
                    <Badge className={categoryMeta.badgeClass}>
                      {categoryConfigs.length} job{categoryConfigs.length === 1 ? "" : "s"}
                    </Badge>
                  </div>
                  <CardDescription>{categoryMeta.description}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="flex items-center gap-4 border-b px-4 py-2 text-xs font-medium text-muted-foreground">
                    <div className="w-4" />
                    <div className="flex-1">Name</div>
                    <div className="hidden min-w-[180px] sm:block">Schedule</div>
                    <div className="hidden min-w-[90px] md:block">Last run</div>
                    <div className="hidden min-w-[170px] lg:block">Next run</div>
                    <div className="hidden min-w-[110px] sm:block">Last status</div>
                    <div className="w-16 text-center">Config</div>
                    <div className="w-48" />
                  </div>
                  {categoryConfigs.map((config) => (
                    <CronRow key={config._id} config={config} />
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
